import Vue from 'vue'
import Vuex from 'vuex'
import { db } from '@/firebase/init'
import * as Sentry from '@sentry/browser'
import { mixCompareVersionsNewerOnly } from '@/globals/javascript/_util/mixins'
import { DB_STATS } from '@/globals/javascript/models/_helper'
import { ENV } from '@/globals/javascript/_util/env'
import { screenings } from './screenings/index'
import { materialData } from './materialData'
import { translationStore } from './translationStore'
import { countryStore } from './countryStore'
import { resourceStore } from './resourceStore'
import { ImageUpload } from './ImageUpload'
import { fileUpload } from './fileUpload'
import { statuses } from './statuses'
import { nextStep } from './nextStep'
import { analyses } from './analyses'
import { admin } from './admin'
import { account } from './account'
import { requisition } from './requisition'
import { user } from './user'
import { globalMenu } from './globalMenu'
import { migrations } from './migrations'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    packageVersion: process.env.PACKAGE_VERSION || '0',
    isDev: ENV.isDevelopment || ENV.isStage,
    isAppInitialized: false,
    isPageLoading: true,
    isLoggedIn: false,
    hasNetworkConnection: true,
    versions: {},
    currentRoute: {},
    prevPages: [],

    // Snapshot listeners
    unsubscribeVersions: null,
  },
  mutations: {
    setPageLoad: (state, newState) => {
      state.isPageLoading = newState
    },
    resetVersions: (state) => {
      state.versions = {}
      if (state.unsubscribeVersions) {
        state.unsubscribeVersions()
        state.unsubscribeVersions = null
      }
    },
    updateAppInitialized: (state, status) => {
      state.isAppInitialized = status
    },
    updateVersions: (state, { data, unsubscribe }) => {
      state.versions = data.versions['milva-pro']
      state.unsubscribeVersions = unsubscribe
    },
    updateUserLoginState: (state, user) => {
      state.isLoggedIn = !!user
    },
    updateCurrentRoute: (state, newRoute) => {
      state.currentRoute = newRoute

      // Sentry
      Sentry.setTag('page-name', newRoute.name || null)
    },
    checkAndUpdatePageScroll: (state, newRoute) => {
      const oldPage = state.prevPages[1]

      // Check to scroll
      if (
        oldPage
        && oldPage.path === newRoute.path
        && oldPage.scrollPos !== null
        && !oldPage.path.includes('/add-type') // To avoid scrolling down when adding a new type
      ) {
        requestAnimationFrame(() => {
          window.scrollTo(0, oldPage.scrollPos)
        })
      }
      else if (state.prevPages.length) {
        requestAnimationFrame(() => {
          window.scrollTo(0, 0)
        })
      }

      // Set new prevPage
      const newPage = {
        path: newRoute.path,
        scrollPos: null,
      }

      state.prevPages.unshift(newPage)
      state.prevPages.splice(2)
    },
    updateScrollBeforeLeave: (state) => {
      if (!state.prevPages.length) {
        return
      }

      state.prevPages[0].scrollPos = window.pageYOffset
    },
    setHasNetworkConnection: (state, newState) => {
      state.setHasNetworkConnection = newState
    },
  },
  actions: {
    setPageLoad: ({ commit }, newState) => {
      commit('setPageLoad', newState)
    },
    setHasNetworkConnection: ({ commit }, newState) => {
      commit('setHasNetworkConnection', newState)
    },
    resetOnLogOut: ({ commit, dispatch }) => {
      commit('resetCurrentUser')
      commit('resetVersions')
      commit('resetAllUsers')
      commit('resetAccount')
      commit('resetAccounts')
      commit('resetHoldensenAccount')
      dispatch('resetAllScreenings')
    },
    getStats: ({ state, commit }) => {
      if (state.unsubscribeVersions) {
        return
      }

      const unsubscribe = db
        .collection(DB_STATS)
        .doc('--stats--')
        .onSnapshot((doc) => {
          commit('updateVersions', { data: doc.data(), unsubscribe })
        })
    },
    updateAppInitialized: ({ commit }, status) => {
      commit('updateAppInitialized', status)
    },
    updateUserLoginState: ({ commit }, user) => {
      commit('updateUserLoginState', user)
    },
    updateCurrentRoute: ({ commit }, newRoute) => {
      commit('updateCurrentRoute', newRoute)
    },
    checkAndUpdatePageScroll: ({ commit }, newRoute) => {
      commit('checkAndUpdatePageScroll', newRoute)
    },
    updateScrollBeforeLeave: ({ commit }) => {
      commit('updateScrollBeforeLeave')
    },
    checkForNewAppVersion: ({ getters }) => new Promise((resolve) => {
      const waitForLoad = setInterval(() => {
        const { packageVersion } = getters
        const { latestAppVersion } = getters

        if (!packageVersion || !latestAppVersion) {
          return
        }

        clearInterval(waitForLoad)

        const isNewVersionAvailable = mixCompareVersionsNewerOnly(
          latestAppVersion,
          packageVersion,
        )

        resolve(isNewVersionAvailable)

        if (!isNewVersionAvailable) {
          return
        }

        // Get latest version
        // We need to put this in a timeout to ensure the page will not keep reloading
        setTimeout(() => {
          const locationArray = window.location.href.split('?')
          window.location.href = `${
            locationArray[0]
          }?version=${
            latestAppVersion
          }-${
            new Date().getTime()
          }`
        }, 500)
      }, 100)
    }),
  },
  getters: {
    // Loaders
    allStatsLoaded: (state) => !!state.unsubscribeVersions,

    // Other
    packageVersion: (state) => state.packageVersion,
    isDev: (state) => state.isDev,
    latestAppVersion: (state) => {
      if (!state.versions || !state.versions.site) {
        return null
      }
      return state.versions.site
    },
    latestChangelogVersion: (state) => {
      if (!state.versions || !state.versions.changelog) {
        return null
      }

      return state.versions.changelog
    },
    isAppInitialized: (state) => state.isAppInitialized,
    isPageLoading: (state) => state.isPageLoading,
    isLoggedIn: (state) => state.isLoggedIn,
    hasNetworkConnection: (state) => state.hasNetworkConnection,
    currentRoute: (state) => state.currentRoute,
  },
  modules: {
    screenings,
    materialData,
    translationStore,
    countryStore,
    resourceStore,
    ImageUpload,
    fileUpload,
    statuses,
    nextStep,
    analyses,
    admin,
    account,
    requisition,
    user,
    globalMenu,
    migrations,
  },
})
